import React from 'react'

import { ROLES } from 'constants/roles'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useUpdateInternalUser, useInternalRoles } from 'hooks/api'
import { Controller, useForm } from 'react-hook-form'
import { Role } from 'types'

import { Stack, Typography } from '@mui/material'

import { Button, Modal, TextField } from '@repo/ui'
import { SelectCompanyField, Select, type SelectItem } from 'components'

interface EditInternalUserModalProps {
  user: {
    id: string
    email: string
    role?: {
      id: string
      name: string
    }
    companies?: Array<{ id: string; name: string; logo: string | null }>
    active?: boolean
  }
  open: boolean
  onClose: () => void
  onUpdated?: () => void
}

export const EditInternalUserModal = ({
  user,
  open,
  onClose,
  onUpdated,
}: EditInternalUserModalProps) => {
  const methods = useForm({
    defaultValues: {
      role: user.role?.name || '',
      company_ids: user.companies?.map(c => c.id) || [],
      active: user.active ?? true,
    },
  })
  const { control, handleSubmit, setValue, watch } = methods

  const selectedRoleName = watch('role') as string
  const isPlatformManager = selectedRoleName === 'platform_manager'
  const isPlatformAdmin = selectedRoleName === 'platform_admin'

  const { open: notification } = useSnackbar()

  const { mutate: updateUser, isPending } = useUpdateInternalUser()

  const { data: internalRolesData, isLoading: isRolesLoading } = useInternalRoles()
  const roles = internalRolesData?.data || []

  const onSubmit = handleSubmit(data => {
    if (!data.role) {
      notification({
        type: 'error',
        title: 'Please select a role',
      })
      return
    }

    const payload = {
      userId: user.id,
      role_name: data.role,
      company_ids: getCompanyIds(data),
      active: data.active,
    }

    updateUser(payload, {
      onSuccess: () => {
        notification({
          type: 'success',
          title: 'User has been updated.',
        })
        onUpdated?.()
        internalOnClose()
      },
      onError: error => {
        const message = getErrorMessage(error)
        notification({
          type: 'error',
          title: message,
        })
      },
    })
  })

  const getCompanyIds = (data: any) => {
    if (isPlatformAdmin) {
      return undefined
    }
    if (isPlatformManager) {
      return data.company_ids
    }
    return data.company_ids
  }

  const onChangeCompany = (value: string[]) => {
    setValue('company_ids', value)
  }

  const internalOnClose = () => {
    onClose()
    methods.reset({
      role: user.role?.name || '',
      company_ids: user.companies?.map(c => c.id) || [],
      active: user.active ?? true,
    })
  }

  const getRoleOptions = (roles: Role[]) => {
    const filteredRoles = roles?.filter(role => role.name.includes('platform'))
    return (
      filteredRoles?.map(role => ({
        label: ROLES[role.name] ?? role.name,
        value: role.name,
      })) ?? []
    )
  }

  const roleOptions: SelectItem[] = React.useMemo(() => getRoleOptions(roles), [roles])

  return (
    <Modal
      title="Edit user"
      open={open}
      onClose={internalOnClose}
      actions={
        <>
          <Button variant="secondary" onClick={internalOnClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending} form="edit-internal-user-form">
            Save Changes
          </Button>
        </>
      }
    >
      <Stack component="form" id="edit-internal-user-form" onSubmit={onSubmit} spacing={4} mt={2}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Email
          </Typography>
          <TextField value={user.email} disabled />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Role
          </Typography>
          <Controller
            name="role"
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  options={roleOptions}
                  value={field.value}
                  placeholder="Select a role"
                  disabled={isRolesLoading || roleOptions.length === 0}
                  onChange={value => field.onChange(value)}
                />
                {fieldState.error && (
                  <Typography variant="body4" color="error">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Stack>

        {isPlatformManager && (
          <SelectCompanyField
            onChange={onChangeCompany}
            defaultValue={watch('company_ids')}
          />
        )}

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Status
          </Typography>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <Select
                options={[
                  { label: 'Active', value: 'true' },
                  { label: 'Inactive', value: 'false' },
                ]}
                value={field.value ? 'true' : 'false'}
                onChange={value => field.onChange(value === 'true')}
              />
            )}
          />
        </Stack>
      </Stack>
    </Modal>
  )
}
